import { ReactElement } from "react";

import BeenhereIcon from "@mui/icons-material/Beenhere";
import DeblurIcon from "@mui/icons-material/Deblur";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Box, Typography } from "@mui/material";

import { buildMenuStructure, CpServices, Icon, MenuEntry } from "@climatepartner/components";

import { ServiceLink } from "../helpers/useServices";
import { getNsTrans } from "../plugins/translations";
import Carousel from "./Carousel";
import ServiceList from "./ServiceList";

type ServiceId = ServiceLink["id"];

interface Props {
    userServiceLinks: CpServices | undefined;
}

const Trans = getNsTrans("homepage-tiles");

interface Tile {
    tileTitle: ReactElement;
    tileIcon: ReactElement;
    tileText: ReactElement;
    sortOrder: number;
}

const topOrderedTiles: Record<ServiceId, Tile> = {
    cfc: {
        tileTitle: <Trans>fpcTitle</Trans>,
        tileIcon: <DeblurIcon color="primary" />,
        tileText: <Trans>fpcDescription</Trans>,
        sortOrder: 0,
    },
    "reduction-homepage-frontend": {
        tileTitle: <Trans>reductionTitle</Trans>,
        tileIcon: <TrendingDownIcon color="primary" />,
        tileText: <Trans>reductionDescription</Trans>,
        sortOrder: 1,
    },
    fpm: {
        tileTitle: <Trans color="primary">fpmTitle</Trans>,
        tileIcon: <Icon color="primary" name="offsetting" />,
        tileText: <Trans>fpmDescription</Trans>,
        sortOrder: 2,
    },
    "transparency-dashboard-frontend": {
        tileTitle: <Trans>transparencyTitle</Trans>,
        tileIcon: <BeenhereIcon color="primary" />,
        tileText: <Trans>transparencyDescription</Trans>,
        sortOrder: 3,
    },
};

const serviceOrderedTiles: Record<ServiceId, Tile> = {
    network: {
        tileTitle: <Trans>networkTitle</Trans>,
        tileIcon: <Icon color="primary" name="supplyNetwork" />,
        tileText: <Trans>networkDescription</Trans>,
        sortOrder: 4,
    },
    "analytics.basic": {
        tileTitle: <Trans>analyticsTitle</Trans>,
        tileIcon: <Icon name="analytics" color="primary" />,
        tileText: <Trans>analyticsDescription</Trans>,
        sortOrder: 5,
    },
    "transparency.reports": {
        tileTitle: <Trans>transparencyReports</Trans>,
        tileIcon: <FactCheckIcon color="primary" />,
        tileText: <Trans>transparencyReportsDescription</Trans>,
        sortOrder: 6,
    },
    "transparency-dashboard-frontend": {
        tileTitle: <Trans>cpCertifiedTitle</Trans>,
        tileIcon: <Icon name="cpCertified" color="primary" />,
        tileText: <Trans>cpCertifiedDescription</Trans>,
        sortOrder: 7,
    },
};

const mapMenuListToTiles = (menuList: MenuEntry[] | undefined, orderedTiles: Record<ServiceId, Tile>) => {
    if (!menuList) {
        return [];
    }

    const tiles = menuList.map((menuEntry) => {
        const tile = orderedTiles[menuEntry.serviceId];

        if (!tile) {
            return null;
        }

        return {
            ...tile,
            url: menuEntry.serviceLink,
        };
    });

    return tiles;
};

export const HomepageTiles = ({ userServiceLinks }: Props) => {
    const menuList = buildMenuStructure(userServiceLinks);
    const carouselTiles = mapMenuListToTiles(menuList?.secondSection, topOrderedTiles);
    const serviceTiles = mapMenuListToTiles(menuList?.restMatched, serviceOrderedTiles);

    return (
        <Box sx={{ marginBottom: 4 }}>
            <Box>
                <Typography variant="h2" sx={{ marginBottom: 2, fontSize: "20px", fontWeight: 500 }}>
                    <Trans>carouselTitle</Trans>
                </Typography>

                <Carousel data={carouselTiles} />
            </Box>

            <Box sx={{ marginTop: 6 }}>
                <Typography variant="h2" sx={{ marginBottom: 2, fontSize: "20px", fontWeight: 500 }}>
                    <Trans>serviceListTitle</Trans>
                </Typography>

                <ServiceList data={serviceTiles} />
            </Box>
        </Box>
    );
};
